import { z } from 'zod'

// The Rails API returns ids as JSON numbers; the app treats ids as strings.
// Accept either and normalize to string so inferred types stay `string`.
export const zId = z.union([z.string(), z.number()]).transform(String)
export const zIdNullable = z.union([z.string(), z.number()]).transform(String).nullable()

export const zListMeta = z.object({
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
  totalPages: z.number(),
})
export type ListMeta = z.infer<typeof zListMeta>

export function listSchema<K extends string, T extends z.ZodTypeAny>(key: K, item: T) {
  return z.object({
    [key]: z.array(item),
    meta: zListMeta,
  } as { [P in K]: z.ZodArray<T> } & { meta: typeof zListMeta })
}

export const zApiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.record(z.array(z.string())).optional(),
  }),
})
export type ApiError = z.infer<typeof zApiError>

// ISO-8601 UTC timestamp, formatted app-side.
export const zIsoDate = z.string()

// Decimal returned by the API as a JSON number (e.g. commissionRate).
export const zDecimal = z.number()

/**
 * Numeric value that Rails sometimes serializes as a string (BigDecimal /
 * ActiveModel). Accepts number or numeric string, normalizes to number.
 * Empty / non-numeric strings → null when used with nullish().
 */
export const zCoerceNumber = z.preprocess((value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isNaN(value) ? null : value
  if (typeof value === 'string') {
    const n = Number(value)
    return Number.isNaN(n) ? value : n
  }
  return value
}, z.number())

// jsonb blobs keep their internal casing untouched (audit-log metadata).
export const zJsonBlob = z.record(z.unknown())
export type JsonBlob = z.infer<typeof zJsonBlob>
